import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { InputField, TextArea } from '../components/Input';
import { Plus, Users } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Volunteer } from '../services/api';
import styles from './VolunteersScreen.module.css';

export function VolunteersScreen() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newVolunteer, setNewVolunteer] = useState({
    role: '',
    hours_planned: '2',
    notes: ''
  });

  useEffect(() => {
    fetchVolunteers();
  }, [eventId]);

  const fetchVolunteers = async () => {
    try {
      const data = await api.volunteers.list(Number(eventId));
      setVolunteers(data);
    } catch (error) {
      console.error('Failed to fetch volunteers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);
    try {
      await api.volunteers.create({
        eventId: eventId,
        role: newVolunteer.role,
        hours_planned: parseFloat(newVolunteer.hours_planned),
        notes: newVolunteer.notes
      });
      setNewVolunteer({ role: '', hours_planned: '2', notes: '' });
      setShowForm(false);
      fetchVolunteers();
    } catch (error) {
      console.error('Failed to create volunteer:', error);
      setActionError('Failed to create volunteer. Check console for details.');
    }
  };

  const updateStatus = async (id: number, status: Volunteer['status']) => {
    setActionError(null);
    try {
      await api.volunteers.updateStatus(id, status);
      fetchVolunteers();
    } catch (error) {
      console.error('Failed to update status:', error);
      setActionError('Failed to update volunteer status. Check console for details.');
    }
  };

  const statusClasses: Record<string, string> = {
    pending: styles.statusPending,
    confirmed: styles.statusConfirmed,
    assigned: styles.statusAssigned,
    completed: styles.statusCompleted,
    cancelled: styles.statusCancelled,
  };

  if (loading) return <div className={styles.container}>Loading...</div>;

  return (
    <div className={styles.container}>
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorClose}>×</button>
        </div>
      )}
      <h1 className={styles.header}>Volunteers</h1>

      {/* Summary cards */}
      <div className={styles.summaryGrid}>
        <Card body={`Total: ${volunteers.length}`} className={styles.textCenter} />
        <Card body={`Completed: ${volunteers.filter(v => v.status === 'completed').length}`} className={styles.textCenter} />
        <Card body={`Pending: ${volunteers.filter(v => v.status === 'pending').length}`} className={styles.textCenter} />
        <Card body={`Planned: ${volunteers.reduce((sum, v) => sum + (v.hours_planned || 0), 0)}h`} className={styles.textCenter} />
      </div>

      {/* Action bar */}
      <div className={styles.actionBar}>
        <h2 className={styles.actionBarTitle}>Volunteer List</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus size={18} />
          Add Volunteer
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className={styles.formContainer}>
          <Card>
            <form onSubmit={handleCreate} className={styles.form}>
              <div>
                <InputField
                  label="Role"
                  value={newVolunteer.role}
                  onChange={(e: any) => setNewVolunteer({ ...newVolunteer, role: e.target.value })}
                  placeholder="e.g., Door Person, Bar Staff, Setup Crew"
                  required
                />
              </div>
              <div>
                <InputField
                  label="Planned Hours"
                  type="number"
                  step="0.5"
                  min="0"
                  value={newVolunteer.hours_planned}
                  onChange={(e: any) => setNewVolunteer({ ...newVolunteer, hours_planned: e.target.value })}
                />
              </div>
              <div>
                <TextArea
                  label="Notes"
                  value={newVolunteer.notes}
                  onChange={(e: any) => setNewVolunteer({ ...newVolunteer, notes: e.target.value })}
                  placeholder="Any special instructions..."
                />
              </div>
              <div className={styles.formActions}>
                <Button type="submit">Add Volunteer</Button>
                <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Volunteer list */}
      <div className={styles.listContainer}>
        {volunteers.map(v => (
          <Card key={v.id}>
            <div className={styles.volunteerHeader}>
              <span className={statusClasses[v.status] || statusClasses.pending}>
                {v.status}
              </span>
              <span className={styles.volunteerName}>{v.member_name || 'Unknown'}</span>
              {v.email && <span className={styles.volunteerEmail}>({v.email})</span>}
            </div>
            <div className={styles.volunteerDetails}>
              <span className={styles.volunteerRole}>{v.role}</span>
              {v.hours_planned > 0 && <span className={styles.detailSeparator}>•</span>}
              {v.hours_planned > 0 && `${v.hours_planned}h planned`}
              {v.hours_shifted > 0 && <span className={styles.detailSeparator}>•</span>}
              {v.hours_shifted > 0 && `${v.hours_shifted}h worked`}
            </div>
            {v.notes && <div className={styles.volunteerNotes}>"{v.notes}"</div>}
            <div className={styles.statusButtons}>
              {user?.role === 'admin' && v.status !== 'pending' && (
                <Button variant="secondary" size="sm" onClick={() => updateStatus(v.id, 'pending')}>Reset</Button>
              )}
              {user?.role === 'admin' && v.status !== 'confirmed' && (
                <Button variant="secondary" size="sm" onClick={() => updateStatus(v.id, 'confirmed')}>Confirm</Button>
              )}
              {user?.role === 'admin' && v.status !== 'assigned' && (
                <Button variant="secondary" size="sm" onClick={() => updateStatus(v.id, 'assigned')}>Assign</Button>
              )}
              {user?.role === 'admin' && v.status !== 'completed' && (
                <Button variant="secondary" size="sm" onClick={() => updateStatus(v.id, 'completed')}>Complete</Button>
              )}
              {v.status !== 'cancelled' && (
                <Button variant="secondary" size="sm" onClick={() => updateStatus(v.id, 'cancelled')}>Cancel</Button>
              )}
            </div>
          </Card>
        ))}
        {volunteers.length === 0 && (
          <div className={styles.emptyState}>
            <Users size={48} className={styles.emptyIcon} />
            <p>No volunteers yet. Be the first to sign up!</p>
          </div>
        )}
      </div>
    </div>
  );
}
